import React from 'react';
import intl from "react-intl-universal";
import { useAppState, useRootStore, useDsManager } from "@stores/AppStateContext";
import { useCrudServices } from "../../../../platform/hooks/useServiceCombinations";

import type {ViewType} from "@stores/WorkspaceStore";

import {showInWorkspace} from "../../../../actions/ui/showInWorkspace";
import {moveToTrashUndoable, restoreFromTrashUndoable} from "../../../../actions/item/trash";
import {moveToArchiveUndoable, restoreFromArchiveUndoable} from "../../../../actions/item/archive";
import {pinItemUndoable, unPinItemUndoable} from "../../../../actions/item/pin";
import { duplicateItemUndoable } from "../../../../actions/item/duplicateItem";
import {ContextMenu} from '../../../components/ContextMenu';

export interface ItemContextMenuProps {
    itemId: string,
    handleClose: () => void,
    parentIdFromTree?: string,
    inEditorView?: boolean,
    index?: number,
}

export const ItemContextMenu = ({itemId, handleClose, parentIdFromTree, index, inEditorView}: ItemContextMenuProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const crudServices = useCrudServices();
    const dsManager = useDsManager();

    const getActions = () => {
        const activeWorkspace = rootStore.view.workspace!;
        const item = activeWorkspace.items.get(itemId)!;
        const view = activeWorkspace.view as ViewType;

        if (view === 'main' || view === 'active' || view === 'recent') {
            return [
                {
                    onClick: () => showInWorkspace(rootStore, itemId, activeWorkspace),
                    text: intl.get('item_context.show_in_workspace').d('Show in Tree'),
                    condition: rootStore.search.textInput !== ''
                },
                {
                    onClick: () => unPinItemUndoable(crudServices, dsManager!, itemId, activeWorkspace),
                    text: intl.get('common.unpin').d('Unpin'),
                    condition: item.pinned
                },
                {
                    onClick: () => pinItemUndoable(crudServices, dsManager!, itemId, activeWorkspace),
                    text: intl.get('common.pin_to_top').d('Pin to top'),
                    condition: !item.pinned
                },
                {
                    onClick: () => {
                        if (!dsManager) {
                            console.error('Data source manager is not ready for duplicate action');
                            return;
                        }
                        duplicateItemUndoable(crudServices, {
                            workspace: activeWorkspace,
                            appState,
                            itemSource: item,
                            parentId: parentIdFromTree,
                            index: index === undefined ? undefined : index + 1,
                        });
                    },
                    text: intl.get('common.duplicate').d('Duplicate'),
                },
                {
                    onClick: () => moveToArchiveUndoable(crudServices, appState, itemId, activeWorkspace),
                    text: intl.get('common.archive_cap').d('Archive'),
                },
                {
                    onClick: () => moveToTrashUndoable(crudServices, appState, itemId, activeWorkspace),
                    text: intl.get('common.delete_cap').d('Delete'),
                },

            ]
        } else if (view === 'trash') {
            return [
                {
                    onClick: () => restoreFromTrashUndoable(crudServices, appState, itemId, activeWorkspace),
                    text: intl.get('item_context.restore').d('Restore'),
                },
                {
                    onClick: () => {
                        activeWorkspace.setConfirmDeleteOpen(`${parentIdFromTree}/${itemId}`);
                        appState.setContextMenu(null);
                    },
                    text: intl.get('item_context.delete_forever').d('Delete Forever'),
                },
            ];
        } else if (view === 'archive') {
            return [
                {
                    onClick: () => restoreFromArchiveUndoable(crudServices, appState, itemId, activeWorkspace),
                    text: intl.get('item_context.unarchive').d('Unarchive'),
                },
                {
                    onClick: () => moveToTrashUndoable(crudServices, appState, itemId, activeWorkspace),
                    text: intl.get('common.delete_cap').d('Delete'),
                },
            ];
        } else {
            throw new Error('unsupported view');
        }
    }

    return (
        <ContextMenu
            isDark={!inEditorView}
            handleClose={handleClose}
            items={getActions()}
        />
    )
}
